import React, { useState } from 'react';
import classnames from 'classnames/bind';
import { Building2, Paperclip, Tag } from 'lucide-react';

import {
    AdminSingleSectionDataVariant,
    AdminSingleSectionData,
    AdminSingleSectionMeta,
} from '@INTERFACES/admin-api/sections';

import useDocumentPermissions from '@HOOKS/useDocumentPermissions';
import { 
    attachmentsAdminApi, 
    useAddTagToVariant, 
    useDeleteTagFromVariant, 
    useCreateAndAddTagToVariant, 
    useGetAllTags 
} from '@SERVICES';

import VariantVendors from '@PAGES/admin/edit-section/components/VariantVendors';
import AttachmentManagement from '@COMPONENTS/SHARED/AttachmentManagement';
import TagsComponent from '@COMPONENTS/COMMON/tags';

import styles from './VariantMetadata.module.scss';

const cx: CX = classnames.bind(styles);

interface Props {
    variant: AdminSingleSectionDataVariant;
    sectionData: AdminSingleSectionData;
    sectionMeta: AdminSingleSectionMeta;
}

type TabType = 'vendors' | 'attachments' | 'tags';

function VariantMetadata({ variant, sectionData, sectionMeta }: Props) {
    const [activeTab, setActiveTab] = useState<TabType>('vendors');

    const { 
        canEditDocument,
        canManageDocumentTags 
    } = useDocumentPermissions({
        documentOwnerId: sectionData.document.owner,
        permissions: sectionMeta.permissions,
    });

    // Tag management hooks
    const [deleteTag, deleteTagResult] = useDeleteTagFromVariant();
    const [addTagToVariant, addTagToVariantResult] = useAddTagToVariant();
    const [createAndAddTagToVariant, createAndAddTagToVariantResult] = useCreateAndAddTagToVariant();

    // Get attachment count
    const { data: attachmentsData } = attachmentsAdminApi.useGetAttachmentsQuery({
        content_type: 'variant',
        object_id: variant.id,
    });
    const attachmentCount = attachmentsData?.length || 0;

    // Get vendor count
    const vendorCount = variant.vendors?.length || 0;

    // Get tags count
    const tagsCount = variant.tags?.length || 0;

    const tabs = [
        {
            id: 'vendors' as TabType,
            label: 'Vendors',
            count: vendorCount,
            icon: <Building2 size={16} />,
            content: (
                <VariantVendors
                    variant={variant}
                    sectionData={sectionData}
                    sectionMeta={sectionMeta}
                />
            ),
        },
        {
            id: 'attachments' as TabType,
            label: 'Attachments',
            count: attachmentCount,
            icon: <Paperclip size={16} />,
            content: (
                <AttachmentManagement
                    parentType="variant"
                    parentId={variant.id}
                    parentName={variant.isDefault ? 'Default variant' : (variant.name || `Variant ${variant.id}`)}
                    canEdit={canEditDocument}
                />
            ),
        },
        {
            id: 'tags' as TabType,
            label: 'Tags',
            count: tagsCount,
            icon: <Tag size={16} />,
            content: (
                <div className={cx('tags-content')}>
                    <TagsComponent
                        locked={!canManageDocumentTags}
                        tags={variant.tags}
                        tagQuery={[deleteTag, deleteTagResult]}
                        apiParams={{
                            chapterId: sectionData.chapter.id,
                            sectionId: sectionData.id,
                            id: variant.id,
                            documentId: sectionData.document.id,
                        }}
                        getAllTagsQuery={useGetAllTags}
                        getTagsParams={{
                            documentId: sectionData.document.id,
                        }}
                        addTagQuery={[addTagToVariant, addTagToVariantResult]}
                        addTagParams={{
                            chapterId: sectionData.chapter.id,
                            sectionId: sectionData.id,
                            id: variant.id,
                        }}
                        createAndAddTagQuery={[createAndAddTagToVariant, createAndAddTagToVariantResult]}
                        createAndAddTagParams={{
                            documentId: sectionData.document.id,
                            chapterId: sectionData.chapter.id,
                            sectionId: sectionData.id,
                            id: variant.id,
                        }}
                        sectionData={{ ...variant, document: sectionData.document }}
                    />
                </div>
            ),
        },
    ];

    return (
        <div className={cx('variant-metadata')}>
            <div className={cx('metadata-header')}>
                <div className={cx('tab-nav')}>
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            className={cx('tab-button', {
                                active: activeTab === tab.id,
                            })}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <span className={cx('tab-icon')}>
                                {tab.icon}
                            </span>
                            <span className={cx('tab-label')}>
                                {tab.label}
                            </span>
                            {tab.count > 0 && (
                                <span className={cx('tab-count')}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <div className={cx('metadata-content')}>
                {tabs.find((tab) => tab.id === activeTab)?.content}
            </div>
        </div>
    );
}

export default VariantMetadata; 